<template>
  <div>
    <form class="addr-form" @submit.prevent="saveAddress">
      <div class="addr-row">
        <div class="field">
          <input
            class="input"
            :class="{ 'input--error': $v.address.zip_code.$error }"
            type="text"
            placeholder="CEP"
            v-mask="'#####-###'"
            v-model="$v.address.zip_code.$model"
          />
          <span v-if="$v.address.zip_code.$error" class="field-error">
            CEP é obrigatório
          </span>
        </div>

        <div class="field">
          <select
            class="input addr-select"
            :class="{ 'input--error': $v.address.state.$error }"
            v-model="$v.address.state.$model"
          >
            <option value="" disabled>Estado</option>
            <option v-for="uf in ufOptions" :key="uf.value" :value="uf.value">
              {{ uf.label }}
            </option>
          </select>
          <span v-if="$v.address.state.$error" class="field-error">
            Estado é obrigatório
          </span>
        </div>
      </div>

      <div class="field">
        <input
          class="input"
          :class="{ 'input--error': $v.address.city.$error }"
          type="text"
          placeholder="Cidade"
          v-model="$v.address.city.$model"
        />
        <span v-if="$v.address.city.$error" class="field-error">
          Cidade é obrigatória
        </span>
      </div>

      <div class="field">
        <input
          class="input"
          :class="{ 'input--error': $v.address.neighborhood.$error }"
          type="text"
          placeholder="Bairro"
          v-model="$v.address.neighborhood.$model"
        />
        <span v-if="$v.address.neighborhood.$error" class="field-error">
          Bairro é obrigatório
        </span>
      </div>

      <div class="addr-row">
        <div class="field addr-street">
          <input
            class="input"
            :class="{ 'input--error': $v.address.street.$error }"
            type="text"
            placeholder="Rua"
            v-model="$v.address.street.$model"
          />
          <span v-if="$v.address.street.$error" class="field-error">
            Rua é obrigatória
          </span>
        </div>

        <div class="field addr-number">
          <input
            class="input"
            :class="{ 'input--error': $v.address.street_number.$error }"
            type="text"
            placeholder="Número"
            v-model="$v.address.street_number.$model"
          />
          <span v-if="$v.address.street_number.$error" class="field-error">
            Número é obrigatório
          </span>
        </div>
      </div>

      <div class="field">
        <input
          class="input"
          type="text"
          placeholder="Complemento (opcional)"
          v-model="$v.address.additional_info.$model"
        />
      </div>

      <button
        type="submit"
        class="btn btn--primary"
        style="width:100%;margin-top:var(--space-2)"
        :disabled="canSaveAddress || isSending"
      >
        {{ isSending ? "Salvando…" : "Salvar endereço" }}
      </button>
    </form>
  </div>
</template>

<script>
import { userComputed, addressMethods } from "./../state/helpers";
import { required } from "vuelidate/lib/validators";
import { UFS } from "../utils/constants";
import axios from "axios";

export default {
  name: "AddressForm",
  data() {
    return {
      isLoading: false,
      isSending: false,
      address: {
        zip_code: "",
        city: "",
        additional_info: "",
        street_number: "",
        street: "",
        state: "",
        neighborhood: ""
      }
    };
  },
  validations: {
    address: {
      zip_code: { required },
      city: { required },
      additional_info: {},
      street_number: { required },
      street: { required },
      state: { required },
      neighborhood: { required }
    }
  },
  mounted() {
    this.fillForm();
  },
  methods: {
    ...addressMethods,
    async saveAddress() {
      if (this.$v.address.$invalid) return;

      try {
        this.isSending = true;
        const action = this.address.id ? "updateAddress" : "createAddress";
        await this[action](this.address);
        this.fillForm();
        this.$emit("addressCreated");
        this.$toast.success("Endereço atualizado com sucesso!");
      } catch (error) {
        console.error("Erro ao salvar endereço:", error);
        this.$toast.error(
          "Erro ao salvar endereço, tente novamente mais tarde!"
        );
      } finally {
        this.isSending = false;
      }
    },
    fillForm() {
      if (this.currentUser && this.currentUser.address.id) {
        this.address = Object.assign({}, this.currentUser.address);
      }
    },
    async fetchByZipCode(code) {
      code = code.replace(/\D/g, "");
      try {
        this.isLoading = true;
        const { data } = await axios.get(
          `https://viacep.com.br/ws/${code}/json/`
        );
        if (!data || data.erro) return;
        const map = {
          street: data.logradouro,
          city: data.localidade,
          state: data.uf,
          neighborhood: data.bairro
        };
        Object.entries(map).forEach(([key, val]) => {
          if (val) this.$v.address[key].$model = val;
        });
      } catch (error) {
        console.error("Erro ao buscar CEP:", error);
      } finally {
        this.isLoading = false;
      }
    }
  },
  computed: {
    ...userComputed,
    canSaveAddress() {
      return this.$v.address.$invalid;
    },
    ufOptions() {
      return UFS.map(uf => ({ label: uf, value: uf }));
    }
  },
  watch: {
    "address.zip_code"(value) {
      if (value.length < 9) return;
      this.fetchByZipCode(value);
    }
  }
};
</script>

<style lang="scss" scoped>
.addr-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.addr-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-3);
}

.addr-street {
  grid-column: span 1;
  flex: 1;
}
.addr-number {
  width: 100px;
}

.addr-row .addr-street + .addr-number {
  width: auto;
}

.addr-row {
  &:last-of-type {
    grid-template-columns: 1fr 100px;
  }
}

.field {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.addr-select {
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none'%3E%3Cpath d='M6 9l6 6 6-6' stroke='%2394a3b8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right var(--space-3) center;
  padding-right: var(--space-8);
  cursor: pointer;
}

.input--error {
  border-color: var(--color-danger);
}

.field-error {
  font-size: var(--font-size-xs);
  color: var(--color-danger);
}
</style>
